/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const heIL: LocalizationResource = {
  locale: 'he-IL',
  apiKeys: {
    action__add: undefined,
    action__search: undefined,
    copySecret: {
      formButtonPrimary__copyAndClose: 'העתק וסגור',
      formHint: 'מסיבות אבטחה, לא נוכל לאפשר לך לצפות בזה שוב מאוחר יותר.',
      formTitle: 'העתק את מפתח ה-API "{{name}}" שלך כעת',
    },
    createdAndExpirationStatus__expiresOn: undefined,
    createdAndExpirationStatus__never: undefined,
    detailsTitle__emptyRow: undefined,
    formButtonPrimary__add: undefined,
    formFieldCaption__expiration__expiresOn: undefined,
    formFieldCaption__expiration__never: undefined,
    formFieldOption__expiration__180d: undefined,
    formFieldOption__expiration__1d: undefined,
    formFieldOption__expiration__1y: undefined,
    formFieldOption__expiration__30d: undefined,
    formFieldOption__expiration__60d: undefined,
    formFieldOption__expiration__7d: undefined,
    formFieldOption__expiration__90d: undefined,
    formFieldOption__expiration__never: undefined,
    formHint: undefined,
    formTitle: undefined,
    lastUsed__days: undefined,
    lastUsed__hours: undefined,
    lastUsed__minutes: undefined,
    lastUsed__months: undefined,
    lastUsed__seconds: undefined,
    lastUsed__years: undefined,
    menuAction__revoke: undefined,
    revokeConfirmation: {
      confirmationText: undefined,
      formButtonPrimary__revoke: undefined,
      formHint: undefined,
      formTitle: undefined,
      inputLabel: undefined,
    },
    tableHeader__actions: undefined,
    tableHeader__lastUsed: undefined,
    tableHeader__name: undefined,
  },
  backButton: 'חזור',
  badge__activePlan: undefined,
  badge__banned: undefined,
  badge__canceledEndsAt: undefined,
  badge__currentPlan: undefined,
  badge__default: 'ברירת מחדל',
  badge__endsAt: undefined,
  badge__expired: undefined,
  badge__freeTrial: undefined,
  badge__otherImpersonatorDevice: 'מכשיר מחקה אחר',
  badge__pastDueAt: undefined,
  badge__pastDuePlan: undefined,
  badge__primary: 'ראשי',
  badge__renewsAt: undefined,
  badge__requiresAction: 'דורש פעולה',
  badge__startsAt: undefined,
  badge__thisDevice: 'מכשיר זה',
  badge__trialEndsAt: undefined,
  badge__unverified: 'לא מאומת',
  badge__upcomingPlan: undefined,
  badge__userDevice: 'מכשיר משתמש',
  badge__you: 'אתה',
  billing: {
    accountCredit: undefined,
    addPaymentMethod__label: undefined,
    alwaysFree: undefined,
    annually: undefined,
    availableFeatures: undefined,
    billedAnnually: undefined,
    billedAnnuallyOnly: undefined,
    billedMonthly: undefined,
    billedMonthlyOnly: undefined,
    cancelFreeTrial: undefined,
    cancelFreeTrialAccessUntil: undefined,
    cancelFreeTrialTitle: undefined,
    cancelSubscription: undefined,
    cancelSubscriptionAccessUntil: undefined,
    cancelSubscriptionNoCharge: undefined,
    cancelSubscriptionPastDue: undefined,
    cancelSubscriptionTitle: undefined,
    cannotSubscribeMonthly: undefined,
    cannotSubscribeUnrecoverable: undefined,
    checkout: {
      description__paymentSuccessful: undefined,
      description__subscriptionSuccessful: undefined,
      downgradeNotice: undefined,
      emailForm: {
        subtitle: undefined,
        title: undefined,
      },
      lineItems: {
        title__freeTrialEndsAt: undefined,
        title__paymentMethod: undefined,
        title__statementId: undefined,
        title__subscriptionBegins: undefined,
        title__totalPaid: undefined,
      },
      pastDueNotice: undefined,
      perMonth: undefined,
      title: undefined,
      title__paymentSuccessful: undefined,
      title__subscriptionSuccessful: undefined,
      title__trialSuccess: undefined,
      totalDueAfterTrial: undefined,
      totalDuePerPeriod: undefined,
    },
    credit: undefined,
    creditRemainder: undefined,
    defaultFreePlanActive: undefined,
    free: undefined,
    getStarted: undefined,
    highlightedPlanBadge: undefined,
    keepFreeTrial: undefined,
    keepSubscription: undefined,
    manage: undefined,
    manageSubscription: undefined,
    month: undefined,
    monthAbbreviation: undefined,
    monthPerUnit: undefined,
    monthly: undefined,
    pastDue: undefined,
    pay: undefined,
    payerCreditRemainder: undefined,
    paymentMethod: {
      applePayDescription: {
        annual: undefined,
        monthly: undefined,
      },
      dev: {
        anyNumbers: undefined,
        cardNumber: undefined,
        cvcZip: undefined,
        developmentMode: undefined,
        expirationDate: undefined,
        testCardInfo: undefined,
      },
    },
    paymentMethods__label: undefined,
    pricingTable: {
      billingCycle: undefined,
      included: undefined,
      seatCost: {
        additionalSeats: undefined,
        freeUpToSeats: undefined,
        includedSeats: undefined,
        perSeat: undefined,
        tooltip: {
          additionalSeatsEach: undefined,
          firstSeatsIncludedInPlan: undefined,
          freeForUpToSeats: undefined,
        },
        unlimitedSeats: undefined,
        upToSeats: undefined,
      },
    },
    proratedDiscount: undefined,
    prorationCredit: undefined,
    reSubscribe: undefined,
    seatBreakdownIncludedPlural: undefined,
    seatBreakdownIncludedSingular: undefined,
    seatBreakdownPlural: undefined,
    seatBreakdownSingular: undefined,
    seats: undefined,
    seatsWithLimit: undefined,
    seeAllFeatures: undefined,
    startFreeTrial: undefined,
    startFreeTrial__days: undefined,
    subscribe: undefined,
    subscriptionDetails: {
      beginsOn: undefined,
      currentBillingCycle: undefined,
      endsOn: undefined,
      firstPaymentAmount: undefined,
      firstPaymentOn: undefined,
      nextPaymentAmount: undefined,
      nextPaymentOn: undefined,
      pastDueAt: undefined,
      renewsAt: undefined,
      subscribedOn: undefined,
      title: undefined,
      trialEndsOn: undefined,
      trialStartedOn: undefined,
    },
    subtotal: undefined,
    subtotalRenewal: undefined,
    switchPlan: undefined,
    switchToAnnual: undefined,
    switchToAnnualWithAnnualPrice: undefined,
    switchToMonthly: undefined,
    switchToMonthlyWithPrice: undefined,
    totalDue: undefined,
    totalDuePerPeriod: undefined,
    totalDueToday: undefined,
    viewFeatures: undefined,
    viewPayment: undefined,
    year: undefined,
    yearAbbreviation: undefined,
    yearPerUnit: undefined,
  },
  configureSSO: {
    activate: {
      activateButton: undefined,
      activeSubtitle: undefined,
      activeTitle: undefined,
      doneButton: undefined,
      skipButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    changeProviderDialog: {
      cancelButton: undefined,
      confirmButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    configureStep: {
      activeConnectionWarning: {
        dismiss: undefined,
        title: undefined,
      },
      attributeMappingTable: {
        badges: {
          optional: undefined,
          required: undefined,
        },
      },
      samlCustom: {
        assignUsersStep: {
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attributeName: undefined,
              userAttribute: undefined,
            },
            rows: {
              email: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              firstName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              lastName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            paragraph: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
      samlGoogle: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              appAttribute: undefined,
              googleAttribute: undefined,
            },
            rows: {
              email: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              firstName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              lastName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        configureUserAccess: {
          assignUsersInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          headerSubtitle: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataFile: {
            description: undefined,
            fileUploaded: undefined,
            label: undefined,
            removeFile: undefined,
            replaceFile: undefined,
            uploadFile: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataFile: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          nameIdInstructions: {
            step1: undefined,
            step2: undefined,
          },
          paragraph: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          title: undefined,
        },
      },
      samlMicrosoft: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attribute: undefined,
              claimName: undefined,
              value: undefined,
            },
            copyClaimName: undefined,
            copyClaimNameCopied: undefined,
            rows: {
              email: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              firstName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              lastName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          step1: undefined,
          step2: undefined,
          title: undefined,
        },
        createAppStep: {
          assignUsersInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: {
              label: undefined,
              subSteps: {
                appName: undefined,
                create: undefined,
                nonGallery: undefined,
              },
            },
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          step1: undefined,
          step2: undefined,
          step3: undefined,
          step4: undefined,
          step5: undefined,
          step6: undefined,
          title: undefined,
        },
      },
      samlOkta: {
        assignUsersStep: {
          assignUsersInstructions: {
            paragraph: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
          },
          headerSubtitle: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              expression: undefined,
              name: undefined,
            },
            rows: {
              email: {
                expression: undefined,
                name: undefined,
              },
              firstName: {
                expression: undefined,
                name: undefined,
              },
              lastName: {
                expression: undefined,
                name: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        createAppStep: {
          completeSamlIntegrationInstructions: {
            step1: undefined,
            step2: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            serviceProviderFields: {
              acsUrl: {
                label: undefined,
              },
              spEntityId: {
                label: undefined,
              },
            },
            title: undefined,
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
    },
    missingManageEnterpriseConnectionsPermission: {
      subtitle: 'פנה למנהל הארגון שלך כדי לשדרג את ההרשאות שלך.',
      title: 'אין לך הרשאה לנהל כניסה אחידה (SSO)',
    },
    navbar: {
      title: 'הגדרת כניסה אחידה (SSO)',
    },
    organizationDomainsStep: {
      domainCard: {
        badge__expired: undefined,
        badge__unverified: 'לא מאומת',
        badge__verified: 'מאומת',
        expiredAtLabel: undefined,
        expiredLabel: undefined,
        removeButtonTooltip__lastVerifiedDomain: undefined,
        removeButtonTooltip__lastVerifiedDomainActive: undefined,
        txtRecord: {
          hostLabel: 'מארח / שם',
          instructions: 'הוסף רשומת TXT זו אצל ספק ה-DNS שלך. נאמת אותה אוטומטית ברגע שהרשומה תהיה פעילה.',
          typeLabel: 'סוג',
          valueLabel: 'ערך',
        },
        verifiedAtLabel: "אומת בתאריך {{ date | shortDate('he-IL') }}",
        verifyAgainButton: undefined,
      },
      domainSuggestion: {
        formButtonPrimary__add: 'הוסף {{domain}}',
        messageLabel: 'האימייל שלך משתמש ב-{{domain}}. האם ברצונך להוסיף אותו?',
      },
      formButtonPrimary__add: 'הוסף',
      formFieldInputPlaceholder__domain: 'הוסף דומיין',
      formFieldLabel__domain: 'דומיין',
      removeDomainDialog: {
        cancelButton: undefined,
        removeButton: undefined,
        subtitle__active: undefined,
        subtitle__inactive: undefined,
        title: undefined,
      },
      subtitle: 'הוסף ואמת בעלות על הדומיינים שהארגון שלך משתמש בהם כדי להתחבר.',
      title: 'הוספת דומיינים של SSO',
    },
    resetConnectionDialog: {
      cancelButton: undefined,
      confirmationFieldLabel: undefined,
      confirmationFieldPlaceholder: undefined,
      resetButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    selectProviderStep: {
      saml: {
        customSaml: 'ספק SAML מותאם אישית',
        google: undefined,
        groupLabel: 'SAML',
        microsoft: undefined,
        okta: 'Okta Workforce',
      },
      subtitle: 'בחר את הספק שעבורו תגדיר SSO.',
      title: 'בחר ספק',
      warning: 'לאחר בחירת ספק לא ניתן לשנות אותו עד לסיום ההגדרה',
    },
    testConfigurationStep: {
      error__noSuccessfulTestRun: undefined,
      subtitle: undefined,
      testResults: {
        actionLabel__refresh: undefined,
        empty: {
          subtitle: undefined,
          title: undefined,
        },
        polling: undefined,
        status__failed: undefined,
        status__pending: undefined,
        status__success: undefined,
        title: undefined,
      },
      testRunDetails: {
        howToFix: {
          actionLabel__viewDocumentation: undefined,
          oauth_access_denied: {
            description: undefined,
          },
          oauth_fetch_user_error: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
          },
          oauth_token_exchange_error: {
            description: undefined,
          },
          saml_email_address_domain_mismatch: {
            description: undefined,
          },
          saml_response_relaystate_missing: {
            description: undefined,
          },
          saml_user_attribute_missing: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          sectionTitle: undefined,
        },
        parsedUserInfo: {
          email: undefined,
          firstName: undefined,
          sectionTitle: undefined,
        },
        runDetails: {
          actionLabel__copied: undefined,
          actionLabel__copy: undefined,
          errorCode: undefined,
          fullMessage: undefined,
          sectionTitle: undefined,
          status: undefined,
          timestamp: undefined,
        },
        title: undefined,
      },
      testUrl: {
        actionLabel__open: undefined,
      },
      title: undefined,
    },
  },
  createOrganization: {
    formButtonSubmit: 'צור ארגון',
    invitePage: {
      formButtonReset: 'דלג',
    },
    title: 'צור ארגון',
  },
  dates: {
    lastDay: "אתמול ב-{{ date | timeString('iw') }}",
    next6Days: "{{ date | weekday('iw','long') }} ב-{{ date | timeString('iw') }}",
    nextDay: "מחר ב-{{ date | timeString('iw') }}",
    numeric: "{{ date | numeric('iw') }}",
    previous6Days: "{{ date | weekday('iw','long') }} האחרון ב-{{ date | timeString('iw') }}",
    sameDay: "היום ב-{{ date | timeString('iw') }}",
  },
  dividerText: 'או',
  footerActionLink__alternativePhoneCodeProvider: undefined,
  footerActionLink__useAnotherMethod: 'השתמש בשיטה אחרת',
  footerPageLink__help: 'עזרה',
  footerPageLink__privacy: 'פרטיות',
  footerPageLink__terms: 'תנאים',
  formButtonPrimary: 'המשך',
  formButtonPrimary__verify: 'Verify',
  formFieldAction__forgotPassword: 'שכחת סיסמה?',
  formFieldError__matchingPasswords: 'הסיסמאות תואמות.',
  formFieldError__notMatchingPasswords: 'הסיסמאות אינן תואמות.',
  formFieldError__verificationLinkExpired: 'הקישור לאימות פג תוקף. אנא בקש/י קישור חדש.',
  formFieldHintText__optional: 'אופציונלי',
  formFieldHintText__slug: '(slug) הוא מזהה קריא שמיועד להיות ייחודי. הוא משמש לעיתים קרובות בכתובות URL.',
  formFieldInputPlaceholder__apiKeyDescription: undefined,
  formFieldInputPlaceholder__apiKeyExpirationDate: undefined,
  formFieldInputPlaceholder__apiKeyName: undefined,
  formFieldInputPlaceholder__backupCode: undefined,
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'מחיקת חשבון',
  formFieldInputPlaceholder__emailAddress: 'כתובת הדוא"ל שלך',
  formFieldInputPlaceholder__emailAddress_username: undefined,
  formFieldInputPlaceholder__emailAddresses: 'example@email.com, example2@email.com',
  formFieldInputPlaceholder__firstName: 'שם פרטי',
  formFieldInputPlaceholder__lastName: 'שם משפחה',
  formFieldInputPlaceholder__organizationDomain: undefined,
  formFieldInputPlaceholder__organizationDomainEmailAddress: undefined,
  formFieldInputPlaceholder__organizationName: 'שם הארגון',
  formFieldInputPlaceholder__organizationSlug: 'הארגון-שלי',
  formFieldInputPlaceholder__password: 'הזן סיסמה',
  formFieldInputPlaceholder__phoneNumber: 'הזן את מספר הטלפון שלך',
  formFieldInputPlaceholder__signUpPassword: undefined,
  formFieldInputPlaceholder__username: undefined,
  formFieldInput__emailAddress_format: undefined,
  formFieldLabel__apiKey: 'מפתח API',
  formFieldLabel__apiKeyDescription: 'תיאור',
  formFieldLabel__apiKeyExpiration: 'תאריך תפוגה',
  formFieldLabel__apiKeyName: 'שם מפתח סודי',
  formFieldLabel__automaticInvitations: 'הפעל הזמנות אוטומטיות לדומיין הזה',
  formFieldLabel__backupCode: 'קוד גיבוי',
  formFieldLabel__confirmDeletion: 'אישור',
  formFieldLabel__confirmPassword: 'אמת סיסמה',
  formFieldLabel__currentPassword: 'סיסמה נוכחית',
  formFieldLabel__emailAddress: 'כתובת דוא"ל',
  formFieldLabel__emailAddress_username: 'כתובת דוא"ל או שם משתמש',
  formFieldLabel__emailAddresses: 'כתובות דוא"ל',
  formFieldLabel__firstName: 'שם פרטי',
  formFieldLabel__lastName: 'שם משפחה',
  formFieldLabel__newPassword: 'סיסמה חדשה',
  formFieldLabel__organizationDomain: 'דומיין',
  formFieldLabel__organizationDomainDeletePending: 'מחק הזמנות והצעות ממתינות',
  formFieldLabel__organizationDomainEmailAddress: 'כתובת אימייל לאימות',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'הזן כתובת אימייל תחת דומיין זה כדי לקבל קוד ולאמת את הדומיין.',
  formFieldLabel__organizationName: 'שם הארגון',
  formFieldLabel__organizationSlug: 'כתובת URL של הארגון',
  formFieldLabel__passkeyName: 'שם או מפתח סיסמה',
  formFieldLabel__password: 'סיסמה',
  formFieldLabel__phoneNumber: 'מספר טלפון',
  formFieldLabel__role: 'תפקיד',
  formFieldLabel__signOutOfOtherSessions: 'התנתק מכל המכשירים האחרים',
  formFieldLabel__username: 'שם משתמש',
  identityPreviewEditButton__emailAddress: undefined,
  identityPreviewEditButton__identifier: undefined,
  identityPreviewEditButton__phoneNumber: undefined,
  impersonationFab: {
    action__signOut: 'התנתק',
    title: 'מחובר כ{{identifier}}',
  },
  lastAuthenticationStrategy: 'בשימוש אחרון',
  maintenanceMode: undefined,
  membershipRole__admin: 'מנהל',
  membershipRole__basicMember: 'חבר',
  membershipRole__guestMember: 'אורח',
  oauthConsent: {
    action__allow: undefined,
    action__deny: undefined,
    offlineAccessNotice: undefined,
    redirectNotice: undefined,
    redirectUriModal: {
      subtitle: undefined,
      title: undefined,
    },
    scopeList: {
      title: undefined,
    },
    subtitle: undefined,
    viewFullUrl: undefined,
    warning: undefined,
  },
  organizationList: {
    action__createOrganization: 'צור ארגון',
    action__invitationAccept: 'הצטרף',
    action__suggestionsAccept: 'בקשה להצטרפות',
    createOrganization: 'צור ארגון',
    invitationAcceptedLabel: 'הצטרף',
    subtitle: 'כדי להמשיך ל{{applicationName}}',
    suggestionsAcceptedLabel: 'ממתין לאישור',
    title: 'בחר חשבון',
    titleWithoutPersonal: 'בחר ארגון',
  },
  organizationProfile: {
    apiKeysPage: {
      title: undefined,
    },
    badge__automaticInvitation: 'הזמנות אוטומטיות',
    badge__automaticSuggestion: 'הצעות אוטומטיות',
    badge__enterpriseSso: undefined,
    badge__manualInvitation: 'ללא הרשמה אוטומטית',
    badge__unverified: 'לא מאומת',
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentMethodsSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeMethod: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__payerCredit: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        includedSeatsUsage: undefined,
        overview: undefined,
        paidSeatsUsage: undefined,
        seatLimit: undefined,
        seatLimitAndIncludedSeats: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    createDomainPage: {
      subtitle:
        'הוסף את הדומיין לאימות. משתמשים עם כתובות אימייל תחת דומיין זה יכולים להצטרף לארגון באופן אוטומטי או לבקש להצטרף.',
      title: 'הוסף דומיין',
    },
    invitePage: {
      detailsTitle__inviteFailed: 'לא היה ניתן לשלוח את ההזמנות. תקן את הבעיות הבאות ונסה שוב:',
      formButtonPrimary__continue: 'שלח הזמנות',
      formButtonPrimary__purchaseSeats: undefined,
      selectDropdown__role: 'בחר תפקיד',
      subtitle: 'הזמן חברים חדשים לארגון זה',
      successMessage: 'ההזמנות נשלחו בהצלחה',
      title: 'הזמן חברים',
    },
    membersPage: {
      action__invite: 'הזמן',
      action__search: undefined,
      activeMembersTab: {
        menuAction__remove: 'הסר חבר',
        tableHeader__actions: undefined,
        tableHeader__joined: 'הצטרף',
        tableHeader__role: 'תפקיד',
        tableHeader__user: 'משתמש',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: 'אנחנו מעדכנים את התפקידים הזמינים. לאחר שזה יסתיים, תוכל לעדכן תפקידים שוב.',
          title: 'התפקידים נעולים זמנית',
        },
      },
      detailsTitle__emptyRow: 'אין חברים להצגה',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'הזמן משתמשים על ידי חיבור דומיין אימייל לארגון שלך. כל מי שנרשם עם דומיין אימייל תואם יוכל להצטרף לארגון בכל עת.',
          headerTitle: 'הזמנות אוטומטיות',
          primaryButton: 'נהל דומיינים מאומתים',
        },
        table__emptyRow: 'אין הזמנות להצגה',
      },
      invitedMembersTab: {
        menuAction__revoke: 'בטל הזמנה',
        tableHeader__invited: 'הוזמן',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle: 'משתמשים שנרשמים עם דומיין אימייל תואם יוכלו לראות הצעה לבקש להצטרף לארגון שלך.',
          headerTitle: 'הצעות אוטומטיות',
          primaryButton: 'נהל דומיינים מאומתים',
        },
        menuAction__approve: 'אשר',
        menuAction__reject: 'דחה',
        tableHeader__requested: 'בקשת גישה',
        table__emptyRow: 'אין בקשות להצגה',
      },
      start: {
        headerTitle__invitations: 'הזמנות',
        headerTitle__members: 'חברים',
        headerTitle__requests: 'בקשות',
      },
    },
    navbar: {
      apiKeys: undefined,
      billing: undefined,
      description: 'נהל את הארגון שלך.',
      general: 'כללי',
      members: 'חברים',
      security: undefined,
      title: 'ארגון',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: undefined,
        planMembershipLimitExceeded: undefined,
      },
      title: undefined,
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'הקלד "{{organizationName}}" למטה כדי להמשיך.',
          messageLine1: 'האם אתה בטוח שאתה רוצה למחוק את הארגון הזה?',
          messageLine2: 'פעולה זו היא סופית ובלתי הפיכה.',
          successMessage: 'מחקת את הארגון.',
          title: 'מחק ארגון',
        },
        leaveOrganization: {
          actionDescription: 'הקלד "{{organizationName}}" למטה כדי להמשיך.',
          messageLine1: 'האם אתה בטוח שאתה רוצה לעזוב את הארגון הזה? תאבד גישה לארגון זה וליישומים שלו.',
          messageLine2: 'פעולה זו היא סופית ובלתי הפיכה.',
          successMessage: 'עזבת את הארגון.',
          title: 'עזוב את הארגון',
        },
        title: 'סיכון',
      },
      domainSection: {
        menuAction__manage: 'נהל',
        menuAction__remove: 'מחק',
        menuAction__verify: 'אמת',
        primaryButton: 'הוסף דומיין',
        subtitle: 'אפשר למשתמשים להצטרף לארגון באופן אוטומטי או לבקש להצטרף על בסיס דומיין אימייל מאומת.',
        title: 'דומיינים מאומתים',
      },
      successMessage: 'הארגון עודכן.',
      title: 'פרופיל ארגון',
    },
    removeDomainPage: {
      messageLine1: 'דומיין האימייל {{domain}} יוסר.',
      messageLine2: 'משתמשים לא יוכלו להצטרף לארגון באופן אוטומטי לאחר פעולה זו.',
      successMessage: '{{domain}} הוסר.',
      title: 'הסר דומיין',
    },
    securityPage: {
      removeDialog: {
        confirmButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
      ssoSection: {
        badge__active: undefined,
        badge__inProgress: undefined,
        badge__inactive: undefined,
        badge__unconfigured: undefined,
        descriptionLine1: undefined,
        domainLabel: undefined,
        menuAction__activate: undefined,
        menuAction__deactivate: undefined,
        menuAction__edit: undefined,
        menuAction__remove: undefined,
        primaryButton__continueConfiguration: undefined,
        primaryButton__startConfiguration: undefined,
        title: undefined,
        tooltip: undefined,
        tooltipLabel: undefined,
        tooltip__noRole: undefined,
      },
      title: undefined,
    },
    start: {
      headerTitle__general: 'כללי',
      headerTitle__members: 'חברים',
      membershipSeatUsageLabel: undefined,
      profileSection: {
        primaryButton: 'עדכן פרופיל',
        title: 'פרופיל ארגון',
        uploadAction__title: 'לוגו',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'הסרת דומיין זה תשפיע על המשתמשים המוזמנים.',
        removeDomainActionLabel__remove: 'הסר דומיין',
        removeDomainSubtitle: 'הסר דומיין זה מרשימת הדומיינים המאומתים שלך',
        removeDomainTitle: 'הסר דומיין',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'משתמשים מוזמנים אוטומטית להצטרף לארגון כשהם נרשמים ויכולים להצטרף בכל עת.',
        automaticInvitationOption__label: 'הזמנות אוטומטיות',
        automaticSuggestionOption__description:
          'משתמשים מקבלים הצעה לבקש להצטרף, אך חייבים להיות מאושרים על ידי מנהל לפני שיוכלו להצטרף לארגון.',
        automaticSuggestionOption__label: 'הצעות אוטומטיות',
        calloutInfoLabel: 'שינוי מצב ההרשמה ישפיע רק על משתמשים חדשים.',
        calloutInvitationCountLabel: 'הזמנות ממתינות שנשלחו למשתמשים: {{count}}',
        calloutSuggestionCountLabel: 'הצעות ממתינות שנשלחו למשתמשים: {{count}}',
        manualInvitationOption__description: 'משתמשים יכולים להצטרף לארגון רק באמצעות הזמנה ידנית.',
        manualInvitationOption__label: 'ללא הרשמה אוטומטית',
        subtitle: 'בחר כיצד משתמשים מדומיין זה יכולים להצטרף לארגון.',
      },
      start: {
        headerTitle__danger: 'סיכון',
        headerTitle__enrollment: 'אפשרויות הרשמה',
      },
      subtitle: 'הדומיין {{domain}} אומת בהצלחה. המשך בבחירת מצב הרשמה.',
      title: 'עדכן {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'הזן את קוד האימות שנשלח לכתובת האימייל שלך',
      formTitle: 'קוד אימות',
      resendButton: 'לא קיבלת קוד? שלח שוב',
      subtitle: 'הדומיין {{domainName}} צריך להיות מאומת באמצעות אימייל.',
      subtitleVerificationCodeScreen: 'קוד אימות נשלח ל{{emailAddress}}. הזן את הקוד כדי להמשיך.',
      title: 'אמת דומיין',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: undefined,
    action__createOrganization: 'צור ארגון',
    action__invitationAccept: 'הצטרף',
    action__manageOrganization: 'נהל ארגון',
    action__openOrganizationSwitcher: undefined,
    action__suggestionsAccept: 'בקשה להצטרפות',
    notSelected: 'לא נבחר ארגון',
    personalWorkspace: 'אזור אישי',
    suggestionsAcceptedLabel: 'ממתין לאישור',
  },
  paginationButton__next: 'הבא',
  paginationButton__previous: 'הקודם',
  paginationRowText__displaying: 'מציג',
  paginationRowText__of: 'מתוך',
  reverification: {
    alternativeMethods: {
      actionLink: 'השג עזרה',
      actionText: 'אין לך אף אחד מאלה?',
      blockButton__backupCode: 'השתמש בקוד גיבוי',
      blockButton__emailCode: 'קוד אימייל ל {{identifier}}',
      blockButton__passkey: undefined,
      blockButton__password: 'המשך עם הסיסמה שלך',
      blockButton__phoneCode: 'שלח קוד SMS ל {{identifier}}',
      blockButton__totp: 'השתמש באפליקציית האימות שלך',
      getHelp: {
        blockButton__emailSupport: 'תמיכה באימייל',
        content: 'אם יש לך בעיה באימות חשבונך, שלח לנו אימייל ואנחנו ניצור איתך קשר על מנת לשחזר את הגישה בהקדם האפשרי',
        title: 'השג עזרה',
      },
      subtitle: 'נתקלת בבעיות? אתה יכול להשתמש בכל אחת מהשיטות הללו עבור אימות',
      title: 'השתמש בשיטה אחרת',
    },
    backupCodeMfa: {
      subtitle: 'קוד הגיבוי שלך הוא הקוד שקיבלת כאשר הגדרת את האימות הדו-שלבי',
      title: 'הכנס את קוד הגיבוי',
    },
    emailCode: {
      formTitle: 'קוד אימות',
      resendButton: 'לא קיבלת קוד? שלח שוב',
      subtitle: 'המשך ל {{applicationName}}',
      title: 'בדוק את האימייל שלך',
    },
    noAvailableMethods: {
      message: 'לא ניתן להמשיך עם האימות. אין גורם אימות זמין',
      subtitle: 'קרתה תקלה',
      title: 'לא ניתן לאמת את חשבונך',
    },
    passkey: {
      blockButton__passkey: undefined,
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: 'השתמש בשיטה אחרת',
      subtitle: 'הכנס את הסיסמה המקושרת עם חשבונך',
      title: 'הכנס סיסמה',
    },
    phoneCode: {
      formTitle: 'קוד אימות',
      resendButton: 'לא קיבלת קוד? שלח שוב',
      subtitle: 'להמשך ל {{applicationName}}',
      title: 'בדוק את הטלפון שלך',
    },
    phoneCodeMfa: {
      formTitle: 'קוד אימות',
      resendButton: 'לא קיבלת קוד? שלח שוב',
      subtitle: 'להמשך, אנא הכנס את קוד האימות שנשלח לטלפון שלך',
      title: 'בדוק את הטלפון שלך',
    },
    totpMfa: {
      formTitle: 'קוד אימות',
      subtitle: 'להמשך, אנא הכנס את קוד האימות שנוצר על ידי אפליקציית האימות שלך',
      title: 'אימות דו-שלבי',
    },
  },
  searchInput: {
    action__clear: undefined,
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'הוסף חשבון',
      action__signOutAll: 'התנתק מכל החשבונות',
      subtitle: 'בחר את החשבון איתו תרצה להמשיך.',
      title: 'בחר חשבון',
    },
    alternativeMethods: {
      actionLink: 'קבל עזרה',
      actionText: 'אין לך אף אחת מהשיטות האלה?',
      blockButton__backupCode: 'השתמש בקוד גיבוי',
      blockButton__emailCode: 'שלח קוד באימייל ל-{{identifier}}',
      blockButton__emailLink: 'שלח קישור באימייל ל-{{identifier}}',
      blockButton__passkey: 'היכנס עם המפתח שלך',
      blockButton__password: 'התחבר עם הסיסמה שלך',
      blockButton__phoneCode: 'שלח קוד SMS ל-{{identifier}}',
      blockButton__totp: 'השתמש באפליקציית האימות שלך',
      getHelp: {
        blockButton__emailSupport: 'מייל לתמיכה',
        content: 'אם אתה נתקל בקשיים בהתחברות לחשבונך, שלח לנו מייל ונעבוד איתך כדי לשחזר את הגישה בהקדם האפשרי.',
        title: 'קבל עזרה',
      },
      subtitle: 'נתקלת בבעיה? תוכל להשתמש באחת מהשיטות האלה כדי להתחבר.',
      title: 'השתמש בשיטה אחרת',
    },
    alternativePhoneCodeProvider: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: 'להמשיך אל {{applicationName}}',
      title: 'הכנס קוד גיבוי',
    },
    emailCode: {
      formTitle: 'קוד אימות',
      resendButton: 'שלח קוד שוב',
      subtitle: 'להמשיך אל {{applicationName}}',
      title: 'בדוק את הדוא"ל שלך',
    },
    emailCodeMfa: {
      formTitle: 'בדוק את הדוא"ל שלך',
      resendButton: 'לא קיבלת קוד? שלח שוב',
      subtitle: 'להמשיך אל {{applicationName}}',
      title: 'בדוק את הדוא"ל שלך',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'להמשך, פתח את קוד האימות מהמכשיר והדפדפן ממנו אתה מתכוון לבצע כניסה',
        title: 'קישור האימות הזה לא חוקי עבור מכשיר זה',
      },
      expired: {
        subtitle: 'חזור לכרטיסייה המקורית להמשך.',
        title: 'קישור האימות הזה פג תוקף',
      },
      failed: {
        subtitle: 'חזור לכרטיסייה המקורית להמשך.',
        title: 'קישור האימות הזה לא חוקי',
      },
      formSubtitle: 'השתמש בקישור האימות שנשלח לדוא"ל שלך',
      formTitle: 'קישור אימות',
      loading: {
        subtitle: 'תועבר בקרוב',
        title: 'מתחבר...',
      },
      resendButton: 'שלח קישור שוב',
      subtitle: 'להמשיך אל {{applicationName}}',
      title: 'בדוק את הדוא"ל שלך',
      unusedTab: {
        title: 'אתה יכול לסגור את הכרטיסייה הזו',
      },
      verified: {
        subtitle: 'תועבר בקרוב',
        title: 'נכנסת בהצלחה',
      },
      verifiedSwitchTab: {
        subtitle: 'חזור לכרטיסייה המקורית להמשך',
        subtitleNewTab: 'חזור לכרטיסייה שנפתחה חדשה להמשך',
        titleNewTab: 'נכנס בכרטיסייה אחרת',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'השתמש בקישור האימות שנשלח לדוא״ל שלך',
      resendButton: 'לא קיבלת את הקישור? שלח שוב',
      subtitle: 'כדי להמשיך ל-{{applicationName}}',
      title: 'בדוק את הדוא״ל שלך',
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    forgotPassword: {
      formTitle: 'אפס קוד הסיסמה',
      resendButton: 'שלח קוד שוב',
      subtitle: 'כדי לאפס את הסיסמה שלך',
      subtitle_email: 'ראשית, הכנס את הקוד שנשלח לאימייל שלך',
      subtitle_phone: 'ראשית, הכנס את הקוד שנשלח לטלפון שלך',
      title: 'איפוס סיסמה',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'אפס את הסיסמה שלך',
      label__alternativeMethods: 'או, התחבר באמצעות שיטה אחרת.',
      title: 'שכחת סיסמה?',
    },
    newDeviceVerificationNotice: 'אתה נכנס מכשיר חדש. אנו מבקשים אימות כדי לשמור על החשבון שלך מאובטח.',
    noAvailableMethods: {
      message: 'לא ניתן להמשיך בהתחברות. אין גורם אימות זמין.',
      subtitle: 'אירעה שגיאה',
      title: 'לא ניתן להתחבר',
    },
    passkey: {
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: 'השתמש בשיטה אחרת',
      subtitle: 'להמשיך אל {{applicationName}}',
      title: 'הכנס את סיסמתך',
    },
    passwordCompromised: {
      title: undefined,
    },
    passwordPwned: {
      title: undefined,
    },
    passwordUntrusted: {
      title: undefined,
    },
    phoneCode: {
      formTitle: 'קוד אימות',
      resendButton: 'שלח את הקוד שוב',
      subtitle: 'להמשיך אל {{applicationName}}',
      title: 'בדוק את הטלפון שלך',
    },
    phoneCodeMfa: {
      formTitle: 'קוד אימות',
      resendButton: 'שלח את הקוד שוב',
      subtitle: undefined,
      title: 'בדוק את הטלפון שלך',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    resetPassword: {
      formButtonPrimary: 'אפס סיסמה',
      requiredMessage: 'מטעמי אבטחה, יש לאפס את הסיסמה שלך.',
      successMessage: 'הסיסמה שלך שונתה בהצלחה. מחבר אותך, אנא המתן רגע.',
      title: 'אפס סיסמה',
    },
    resetPasswordMfa: {
      detailsLabel: 'אנחנו צריכים לאמת את זהותך לפני שנאפס את הסיסמה שלך.',
    },
    start: {
      actionLink: 'הרשמה',
      actionLink__join_waitlist: undefined,
      actionLink__use_email: 'השתמש בדוא"ל',
      actionLink__use_email_username: 'השתמש בדוא"ל או שם משתמש',
      actionLink__use_passkey: 'השתמש במפתח סיסמה במקום',
      actionLink__use_phone: 'השתמש בטלפון',
      actionLink__use_username: 'השתמש בשם משתמש',
      actionText: 'אין לך חשבון?',
      actionText__join_waitlist: undefined,
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'להמשיך אל {{applicationName}}',
      subtitleCombined: undefined,
      title: 'התחבר',
      titleCombined: undefined,
    },
    totpMfa: {
      formTitle: 'קוד אימות',
      subtitle: 'להמשך, אנא הכנס את קוד האימות שנוצר על ידי אפליקציית האימות שלך',
      title: 'אימות שני שלבים',
    },
    web3Solana: {
      subtitle: 'בחר/י ארנק למטה כדי להתחבר',
      title: 'התחברות עם Solana',
    },
  },
  signInEnterPasswordTitle: 'הזן את הסיסמה שלך',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    continue: {
      actionLink: 'התחבר',
      actionText: 'יש לך חשבון?',
      subtitle: 'להמשיך אל {{applicationName}}',
      title: 'מלא שדות חסרים',
    },
    emailCode: {
      formSubtitle: 'הכנס את קוד האימות שנשלח לכתובת הדוא"ל שלך',
      formTitle: 'קוד אימות',
      resendButton: 'שלח קוד שוב',
      subtitle: 'להמשיך אל {{applicationName}}',
      title: 'אמת את כתובת הדוא"ל שלך',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'להמשך, פתח את קוד האימות מהמכשיר והדפדפן ממנו אתה מתכוון לבצע את הרישום',
        title: 'קישור האימות אינו חוקי עבור מכשיר זה',
      },
      formSubtitle: 'השתמש בקישור האימות שנשלח לכתובת הדוא"ל שלך',
      formTitle: 'קישור לאימות',
      loading: {
        title: 'מתחיל להירשם...',
      },
      resendButton: 'שלח קישור שוב',
      subtitle: 'להמשיך אל {{applicationName}}',
      title: 'אמת את כתובת הדוא"ל שלך',
      verified: {
        title: 'נרשמת בהצלחה',
      },
      verifiedSwitchTab: {
        subtitle: 'חזור לכרטיסייה שנפתחה לאחרונה להמשיך',
        subtitleNewTab: 'חזור לכרטיסייה הקודמת להמשיך',
        title: 'אימות דוא"ל הצליח',
      },
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: undefined,
        label__onlyTermsOfService: undefined,
        label__termsOfServiceAndPrivacyPolicy: undefined,
      },
      continue: {
        subtitle: undefined,
        title: undefined,
      },
    },
    phoneCode: {
      formSubtitle: 'הכנס את קוד האימות שנשלח למספר הטלפון שלך',
      formTitle: 'קוד אימות',
      resendButton: 'שלח קוד שוב',
      subtitle: 'להמשיך אל {{applicationName}}',
      title: 'אמת את מספר הטלפון שלך',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    restrictedAccess: {
      actionLink: undefined,
      actionText: undefined,
      blockButton__emailSupport: undefined,
      blockButton__joinWaitlist: undefined,
      subtitle: undefined,
      subtitleWaitlist: undefined,
      title: undefined,
    },
    start: {
      actionLink: 'התחבר',
      actionLink__use_email: undefined,
      actionLink__use_phone: undefined,
      actionText: 'יש לך חשבון?',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'להמשיך אל {{applicationName}}',
      subtitleCombined: 'להמשיך אל {{applicationName}}',
      title: 'צור את החשבון שלך',
      titleCombined: 'צור את החשבון שלך',
    },
    web3Solana: {
      subtitle: 'בחר/י ארנק למטה כדי להירשם',
      title: 'הרשמה עם Solana',
    },
  },
  socialButtonsBlockButton: 'המשך עם {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'ארגון כבר קיים עבור שם החברה שזוהה ({{organizationName}}) ו-{{organizationDomain}}. הצטרף באמצעות הזמנה.',
    },
    chooseOrganization: {
      action__createOrganization: 'צור ארגון חדש',
      action__invitationAccept: 'הצטרף',
      action__suggestionsAccept: 'בקש להצטרף',
      subtitle: 'הצטרף לארגון קיים או צור חדש',
      subtitle__createOrganizationDisabled: 'הצטרף לארגון קיים',
      suggestionsAcceptedLabel: 'ממתין לאישור',
      title: 'בחר ארגון',
    },
    createOrganization: {
      formButtonReset: 'ביטול',
      formButtonSubmit: 'המשך',
      formFieldInputPlaceholder__name: 'הארגון שלי',
      formFieldInputPlaceholder__slug: 'ha-irgun-sheli',
      formFieldLabel__name: 'שם',
      formFieldLabel__slug: 'Slug',
      subtitle: 'הזן את פרטי הארגון שלך כדי להמשיך',
      title: 'הגדר את הארגון שלך',
    },
    organizationCreationDisabled: {
      subtitle: 'פנה למנהל הארגון שלך לקבלת הזמנה.',
      title: 'עליך להשתייך לארגון',
    },
    signOut: {
      actionLink: 'התנתק',
      actionText: 'מחובר כ-{{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    subtitle: undefined,
    title: undefined,
  },
  taskSetupMfa: {
    badge: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: undefined,
        infoText: undefined,
      },
      addPhoneNumber: undefined,
      cancel: undefined,
      subtitle: undefined,
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyPhone: {
        formButtonPrimary: undefined,
        formTitle: undefined,
        resendButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
    start: {
      methodSelection: {
        phoneCode: undefined,
        totp: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: undefined,
        buttonUnableToScan__nonPrimary: undefined,
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        infoText__ableToScan: undefined,
        infoText__unableToScan: undefined,
        inputLabel__unableToScan1: undefined,
      },
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyTotp: {
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        formTitle: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
  },
  unstable__errors: {
    action_blocked: undefined,
    already_a_member_in_organization: '{{email}} כבר חבר בארגון',
    api_key_name_already_exists: undefined,
    api_key_usage_exceeded: undefined,
    avatar_file_size_exceeded: 'גודל הקובץ חורג מהמגבלה המקסימלית של 10MB. אנא בחר קובץ קטן יותר.',
    avatar_file_type_invalid: 'סוג הקובץ אינו נתמך. אנא העלה תמונה בפורמט JPG, PNG, GIF או WEBP.',
    captcha_invalid: 'ההרשמה נכשלה עקב כשל באימות האבטחה. אנא רענן את הדף ונסה שוב, או פנה לתמיכה לעזרה נוספת.',
    captcha_unavailable: 'ההרשמה נכשלה עקב כשל באימות נגד בוטים. אנא רענן את הדף ונסה שוב, או פנה לתמיכה לעזרה נוספת.',
    form_code_incorrect: undefined,
    form_email_address_blocked:
      'שירותי אימייל זמניים אינם נתמכים. אנא השתמש בכתובת האימייל הרגילה שלך כדי ליצור חשבון.',
    form_identifier_exists__email_address: 'כתובת המייל הזאת כבר תפוסה. אנא נסה אחרת.',
    form_identifier_exists__phone_number: 'מספר הטלפון הזה כבר תפוס. אנא נסה מספר אחר.',
    form_identifier_exists__username: 'שם המשתמש הזה כבר תפוס. אנא נסה שם משתמש אחר',
    form_identifier_not_found: 'לא ניתן למצוא חשבון עם אלו הפרטים.',
    form_new_password_matches_current: undefined,
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: 'כתובת האימייל חייבת להיות כתובת אימייל תקינה.',
    form_param_format_invalid__phone_number: 'מספר הטלפון חייב להיות בפורמט בינלאומי תקין.',
    form_param_max_length_exceeded__first_name: 'שם פרטי לא צריך לעלות על 256 תווים.',
    form_param_max_length_exceeded__last_name: 'שם משפחה לא צריך לעלות על 256 תווים.',
    form_param_max_length_exceeded__name: 'שם לא צריך לעלות על 256 תווים.',
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: undefined,
    form_password_not_strong_enough: 'הסיסמה שלך אינה מספיק חזקה.',
    form_password_or_identifier_incorrect: 'הסיסמה או כתובת האימייל שגויים. נסה שוב או השתמש בשיטה אחרת.',
    form_password_pwned: 'הסיסמה הזו נמצאה כחלק מהפרטים שנחשפו בהפרת נתונים ולא ניתן להשתמש בה, נסה סיסמה אחרת במקום.',
    form_password_pwned__sign_in:
      'הסיסמה הזו נמצאה כחלק מהפרטים שנחשפו בהפרת נתונים ולא ניתן להשתמש בה, אנא בצע איתחול לסיסמה שלך.',
    form_password_size_in_bytes_exceeded:
      'הסיסמה שלך חורגת ממספר הבייטים המרבי המותר, נסה לקצר אותה או להסיר כמה תווים מיוחדים.',
    form_password_untrusted__sign_in: undefined,
    form_password_validation_failed: 'סיסמה שגויה',
    form_username_invalid_character: undefined,
    form_username_invalid_length: undefined,
    form_username_needs_non_number_char: 'שם המשתמש שלך חייב להכיל לפחות תו אחד שאינו מספרי.',
    identification_deletion_failed: 'לא ניתן למחוק את הזיהוי האחרון שלך.',
    insufficient_seats_change_plan: undefined,
    insufficient_seats_contact_support: undefined,
    not_allowed_access:
      "האימייל או מספר הטלפון אינו מותר להרשמה. זה עשוי להיות בגלל השימוש ב-'+', '=', '#' או '.' בכתובת האימייל שלך, השימוש בתחום המחובר לשירות אימייל זמני או הפרסט בכתובת האימייל שלך. אם מחזרים שגיאה, נא ליצור קשר עם תמיכה.",
    oauth_access_denied: undefined,
    organization_domain_blocked: 'הדומיין של ספק האימייל הזה חסום. אנא נסה אחד שונה.',
    organization_domain_common: 'הדומיין של ספק האימייל הזה נפוץ. אנא נסה אחד שונה.',
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded: 'הגעת למגבלת החברות בארגון, כולל הזמנות יוצאות דופן.',
    organization_minimum_permissions_needed: 'חייב להיות חבר ארגון אחד לפחות עם ההרשאות המינימליות הנדרשות.',
    organization_not_found_or_unauthorized: undefined,
    organization_not_found_or_unauthorized_with_create_organization_disabled: undefined,
    passkey_already_exists: 'מפתח הסיסמה כבר רשום במכשיר זה.',
    passkey_not_supported: 'מפתחות סיסמה אינם נתמכים במכשיר זה.',
    passkey_pa_not_supported: 'ההרשמה דורשת מאמת פלטפורמה אך המכשיר אינו תומך בכך.',
    passkey_registration_cancelled: 'רישום מפתח הסיסמה בוטל או פג הזמן הקצוב.',
    passkey_retrieval_cancelled: 'אימות מפתח הסיסמה בוטל או פג הזמן הקצוב.',
    passwordComplexity: {
      maximumLength: 'פחות מ-{{length}} תווים',
      minimumLength: '{{length}} תווים או יותר',
      requireLowercase: 'אות קטנה',
      requireNumbers: 'מספר',
      requireSpecialCharacter: 'תו מיוחד',
      requireUppercase: 'אות גדולה',
      sentencePrefix: 'הסיסמה שלך חייבת להכיל',
    },
    phone_number_exists: 'מספר הטלפון הזה כבר בשימוש. אנא נסה מספר אחר.',
    protect_check_aborted: undefined,
    protect_check_already_resolved: undefined,
    protect_check_execution_failed: undefined,
    protect_check_invalid_script: undefined,
    protect_check_invalid_sdk_url: undefined,
    protect_check_script_load_failed: undefined,
    protect_check_timed_out: undefined,
    protect_check_unsupported_environment: undefined,
    session_exists: 'אתה כבר מחובר לחשבון.',
    web3_missing_identifier: undefined,
    web3_signature_request_rejected: 'דחית את בקשת החתימה. נסה/י שוב כדי להמשיך.',
    web3_solana_signature_generation_failed: 'אירעה שגיאה בעת יצירת החתימה. נסה/י שוב כדי להמשיך.',
    zxcvbn: {
      couldBeStronger: 'הסיסמה שלך תקפה, אך יכולה להיות חזקה יותר. נסה להוסיף יותר תווים.',
      goodPassword: 'עבודה טובה. זו סיסמה מצוינת.',
      notEnough: 'הסיסמה שלך אינה מספיק חזקה.',
      suggestions: {
        allUppercase: 'הגדל כמה, אך לא את כל האותיות.',
        anotherWord: 'הוסף עוד מילים שהן פחות נפוצות.',
        associatedYears: 'הימנע משנים שקשורות אליך.',
        capitalization: 'הגדל יותר מאות אחת.',
        dates: 'המנע מתאריכים ושנים שקשורים אליך.',
        l33t: "המנע מהחלפות תווים צפויות כמו '@' במקום 'a'.",
        longerKeyboardPattern: 'השתמש בדפוסי מקלדת ארוכים יותר ושנה את כיוון ההקלדה מספר פעמים.',
        noNeed: 'אתה יכול ליצור סיסמאות חזקות ללא שימוש בסמלים, מספרים, או אותיות גדולות.',
        pwned: 'אם אתה משתמש בסיסמה זו במקומות אחרים, עליך לשנותה.',
        recentYears: 'הימנע משנים אחרונות.',
        repeated: 'הימנע ממילים ותווים מוחזרים.',
        reverseWords: 'הימנע מכתיבה הפוכה של מילים נפוצות.',
        sequences: 'המנע מרצפות תווים נפוצות.',
        useWords: 'השתמש במילים מרובות, אך הימנע מביטויים נפוצים.',
      },
      warnings: {
        common: 'זו סיסמה שנמצאת בשימוש נפוץ.',
        commonNames: 'שמות נפוצים ושמות משפחה קלים לניחוש.',
        dates: 'תאריכים קלים לניחוש.',
        extendedRepeat: 'דפוסים מוחזרים של תווים כמו "abcabcabc" קלים לניחוש.',
        keyPattern: 'דפוסים קצרים של מקלדת קלים לניחוש.',
        namesByThemselves: 'שמות בודדים או שמות משפחה קלים לניחוש.',
        pwned: 'הסיסמה שלך הוחשפה במהלך הפרת נתונים באינטרנט.',
        recentYears: 'שנים אחרונות קלות לניחוש.',
        sequences: 'רצפות תווים נפוצות כמו "abc" קלות לניחוש.',
        similarToCommon: 'זו סיסמה דומה לסיסמה שנמצאת בשימוש נפוץ.',
        simpleRepeat: 'תווים מוחזרים כמו "aaa" קלים לניחוש.',
        straightRow: 'שורות ישרות של מקשים במקלדת שלך קלות לניחוש.',
        topHundred: 'זו סיסמה שנמצאת בשימוש תכוף.',
        topTen: 'זו סיסמה שנמצאת בשימוש כבד.',
        userInputs: 'אין להזין נתונים אישיים או קשורים לדף.',
        wordByItself: 'מילים בודדות קלות לניחוש.',
      },
    },
  },
  userButton: {
    action__addAccount: 'הוסף חשבון',
    action__closeUserMenu: undefined,
    action__manageAccount: 'נהל חשבון',
    action__openUserMenu: undefined,
    action__signOut: 'התנתק',
    action__signOutAll: 'התנתק מכל החשבונות',
    label__accountActions: 'פעולות חשבון',
    label__activeSessions: 'סשנים פעילים',
    label__userButtonPopover: 'לוח החשבון',
  },
  userProfile: {
    apiKeysPage: {
      title: undefined,
    },
    backupCodePage: {
      actionLabel__copied: 'הועתק!',
      actionLabel__copy: 'העתק הכל',
      actionLabel__download: 'הורד .txt',
      actionLabel__print: 'הדפס',
      infoText1: 'קודי גיבוי יהיו מופעלים לחשבון זה.',
      infoText2: 'שמור את קודי הגיבוי בסוד ואחסן אותם בבטחה. תוכל לחולל מחדש קודי גיבוי אם אתה חושד שהם נפגעו.',
      subtitle__codelist: 'אחסן אותם בבטחה ושמור עליהם בסוד.',
      successMessage:
        'קודי הגיבוי מופעלים כעת. תוכל להשתמש באחד מאלה כדי להתחבר לחשבון שלך, אם אתה מאבד גישה למכשיר האימות שלך. כל קוד יכול להשתמש בו רק פעם אחת.',
      successSubtitle: 'תוכל להשתמש באחד מאלה כדי להתחבר לחשבון שלך, אם אתה מאבד גישה למכשיר האימות שלך.',
      title: 'הוסף אימות קוד גיבוי',
      title__codelist: 'קודי גיבוי',
    },
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentMethodsSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeMethod: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__payerCredit: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        overview: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    connectedAccountPage: {
      formHint: 'בחר ספק כדי לחבר את החשבון שלך.',
      formHint__noAccounts: 'אין ספקים זמינים לחשבונות חיצוניים.',
      removeResource: {
        messageLine1: '{{identifier}} יוסר מהחשבון הזה.',
        messageLine2: 'לא תוכל יותר להשתמש בחשבון מחובר זה וכל התכונות התלויות לא יעבדו.',
        successMessage: '{{connectedAccount}} הוסר מהחשבון שלך.',
        title: 'הסר חשבון מחובר',
      },
      socialButtonsBlockButton: 'חבר חשבון {{provider|titleize}}',
      successMessage: 'הספק התווסף לחשבון שלך',
      title: 'הוסף חשבון מחובר',
    },
    deletePage: {
      actionDescription: 'הקלד "מחיקת חשבון" למטה כדי להמשיך.',
      confirm: 'מחק חשבון',
      messageLine1:
        'האם אתה בטוח שברצונך למחוק את החשבון שלך? חלק מהנתונים המשויכים עשויים להישמר. כדי לבקש מחיקת נתונים מלאה, אנא צור קשר עם התמיכה.',
      messageLine2: 'פעולה זו היא סופית ובלתי הפיכה.',
      title: 'מחק חשבון',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'אימייל שמכיל קוד אימות ישלח לכתובת זו.',
        formSubtitle: 'הכנס את קוד האימות שנשלח ל-{{identifier}}',
        formTitle: 'קוד אימות',
        resendButton: 'שלח קוד מחדש',
        successMessage: 'האימייל {{identifier}} התווסף לחשבון שלך.',
      },
      emailLink: {
        formHint: 'אימייל שמכיל קישור לאימות ישלח לכתובת זו.',
        formSubtitle: 'לחץ על קישור האימות באימייל שנשלח ל-{{identifier}}',
        formTitle: 'קישור לאימות',
        resendButton: 'שלח קישור מחדש',
        successMessage: 'האימייל {{identifier}} התווסף לחשבון שלך.',
      },
      enterpriseSSOLink: {
        formButton: undefined,
        formSubtitle: undefined,
      },
      formHint: undefined,
      removeResource: {
        messageLine1: '{{identifier}} יוסר מהחשבון הזה.',
        messageLine2: 'לא תוכל יותר להתחבר באמצעות כתובת אימייל זו.',
        successMessage: 'האימייל {{emailAddress}} הוסר מהחשבון שלך.',
        title: 'הסר כתובת אימייל',
      },
      title: 'הוסף כתובת אימייל',
      verifyTitle: 'אמת כתובת אימייל',
    },
    formButtonPrimary__add: 'הוסף',
    formButtonPrimary__continue: 'המשך',
    formButtonPrimary__finish: 'סיים',
    formButtonPrimary__remove: 'מחק',
    formButtonPrimary__save: 'שמור',
    formButtonReset: 'בטל',
    mfaPage: {
      formHint: 'בחר שיטה להוספה.',
      title: 'הוסף אימות דו-שלבי',
    },
    mfaPhoneCodePage: {
      backButton: 'השתמש במספר קיים',
      primaryButton__addPhoneNumber: 'הוסף מספר טלפון',
      removeResource: {
        messageLine1: '{{identifier}} לא יקבל יותר קודים לאימות בעת ההתחברות.',
        messageLine2: 'החשבון שלך עשוי לא להיות בטוח כמו שהוא. האם אתה בטוח שאתה רוצה להמשיך?',
        successMessage: 'אימות קוד SMS דו-שלבי הוסר ל{{mfaPhoneCode}}',
        title: 'הסר אימות דו-שלבי',
      },
      subtitle__availablePhoneNumbers: 'בחר מספר טלפון להרשמה לאימות קוד SMS דו-שלבי.',
      subtitle__unavailablePhoneNumbers: 'אין מספרי טלפון זמינים להרשמה לאימות קוד SMS דו-שלבי.',
      successMessage1: 'בעת ההתחברות, תצטרך להזין קוד אימות שנשלח למספר טלפון זה כשלב נוסף.',
      successMessage2:
        'שמור קודי גיבוי אלו במקום בטוח. אם תאבד גישה למכשיר האימות שלך, תוכל להשתמש בקודי גיבוי כדי להתחבר.',
      successTitle: 'אימות קוד SMS הופעל',
      title: 'הוסף אימות קוד SMS',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'סרוק קוד QR במקום',
        buttonUnableToScan__nonPrimary: 'לא יכול לסרוק קוד QR?',
        infoText__ableToScan:
          'הגדר שיטת התחברות חדשה באפליקציית האותנטיקטור שלך וסרוק את קוד ה-QR הבא כדי לחברו לחשבון שלך.',
        infoText__unableToScan: 'הגדר שיטת התחברות חדשה באותנטיקטור שלך והכנס את המפתח שסופק למטה.',
        inputLabel__unableToScan1: 'וודא שסיסמאות מבוססות-זמן או חד-פעמיות מופעלות, ואז סיים לחבר את החשבון שלך.',
        inputLabel__unableToScan2:
          'לחלופין, אם האותנטיקטור שלך תומך בכתובת URI של TOTP, תוכל גם להעתיק את הכתובת המלאה.',
      },
      removeResource: {
        messageLine1: 'קודי האימות מהאותנטיקטור הזה לא יהיו נדרשים יותר בעת ההתחברות.',
        messageLine2: 'החשבון שלך עשוי לא להיות בטוח כמו שהוא. האם אתה בטוח שאתה רוצה להמשיך?',
        successMessage: 'אימות דו-שלבי באמצעות אפליקצית האותנטיקטור הוסר.',
        title: 'הסר אימות דו-שלבי',
      },
      successMessage: 'האימות הדו-שלבי מופעל כעת. בעת ההתחברות, תידרש להכניס קוד אימות מהאותנטיקטור זה כשלב נוסף.',
      title: 'הוסף אפליקציית אימות',
      verifySubtitle: 'הכנס את קוד האימות שנוצר על ידי האותנטיקטור שלך',
      verifyTitle: 'קוד אימות',
    },
    mobileButton__menu: 'תפריט',
    navbar: {
      account: 'פרופיל',
      apiKeys: undefined,
      billing: undefined,
      description: 'נהל את פרטי החשבון שלך.',
      security: 'אבטחה',
      title: 'חשבון',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} יוסר מחשבון זה',
        title: 'הסר מפתח סיסמה',
      },
      subtitle__rename: 'אתה יכול לשנות את שם מפתח הסיסמה על מנת למצוא אותו בקלות יותר',
      title__rename: 'שנה שם למפתח סיסמה',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions: 'מומלץ להתנתק מכל המכשירים האחרים שעלולים היו להשתמש בסיסמה הישנה שלך.',
      readonly: 'כרגע לא ניתן לערוך את הסיסמה שלך מכיוון שניתן להתחבר רק דרך חיבור ארגוני.',
      successMessage__set: 'הסיסמה שלך הוגדרה.',
      successMessage__signOutOfOtherSessions: 'כל המכשירים האחרים התנתקו.',
      successMessage__update: 'הסיסמה שלך עודכנה.',
      title__set: 'הגדר סיסמה',
      title__update: 'שנה סיסמה',
    },
    phoneNumberPage: {
      infoText: 'הודעת טקסט שמכילה קישור לאימות תישלח למספר טלפון זה.',
      removeResource: {
        messageLine1: '{{identifier}} יוסר מהחשבון הזה.',
        messageLine2: 'לא תוכל יותר להתחבר באמצעות מספר טלפון זה.',
        successMessage: '{{phoneNumber}} הוסר מהחשבון שלך.',
        title: 'הסר מספר טלפון',
      },
      successMessage: '{{identifier}} התווסף לחשבון שלך.',
      title: 'הוסף מספר טלפון',
      verifySubtitle: 'הזן את קוד האימות שנשלח ל{{identifier}}',
      verifyTitle: 'אמת מספר טלפון',
    },
    plansPage: {
      title: undefined,
    },
    profilePage: {
      fileDropAreaHint: 'העלה תמונה בפורמט JPG, PNG, GIF, או WEBP הקטנה מ-10 מ"ב',
      imageFormDestructiveActionSubtitle: 'הסר תמונה',
      imageFormSubtitle: 'העלה תמונה',
      imageFormTitle: 'תמונת פרופיל',
      readonly: 'פרטי הפרופיל שלך ניתנים על ידי החיבור הארגוני ואינם ניתנים לעריכה.',
      successMessage: 'הפרופיל שלך עודכן.',
      title: 'עדכן פרופיל',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'התנתק מהמכשיר',
        title: 'מכשירים פעילים',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'נסה שוב',
        actionLabel__reauthorize: 'אשר עכשיו',
        destructiveActionTitle: 'הסר',
        primaryButton: 'חבר חשבון',
        subtitle__disconnected: 'החשבון הזה נותק',
        subtitle__reauthorize:
          'ההרשאות הנדרשות עודכנו, ייתכן שאתה חווה פונקציונליות מוגבלת. אנא אשר מחדש את האפליקציה כדי להימנע מבעיות.',
        title: 'חשבונות מחוברים',
      },
      dangerSection: {
        deleteAccountButton: 'מחק חשבון',
        title: 'מסוכן',
      },
      emailAddressesSection: {
        destructiveAction: 'הסר כתובת אימייל',
        detailsAction__nonPrimary: 'הגדר כעיקרית',
        detailsAction__primary: 'השלם אימות',
        detailsAction__unverified: 'השלם אימות',
        primaryButton: 'הוסף כתובת אימייל',
        title: 'כתובת אימייל',
      },
      enterpriseAccountsSection: {
        primaryButton: 'חבר חשבון',
        title: 'חשבונות ארגוניים',
      },
      headerTitle__account: 'חשבון',
      headerTitle__security: 'אבטחה',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'צור קודים מחדש',
          headerTitle: 'קוד גיבוי',
          subtitle__regenerate: 'קבל קבוצה חדשה של קודי גיבוי מאובטחים. קודי גיבוי קודמים יימחקו ולא ניתן להשתמש בהם.',
          title__regenerate: 'צור מחדש קודי גיבוי',
        },
        phoneCode: {
          actionLabel__setDefault: 'הגדר כברירת מחדל',
          destructiveActionLabel: 'הסר מספר טלפון',
        },
        primaryButton: 'הוסף אימות דו-שלבי',
        title: 'אימות דו-שלבי',
        totp: {
          destructiveActionTitle: 'הסר',
          headerTitle: 'אפליקציית מאמת',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'הגדר סיסמה',
        menuAction__rename: 'עדכן סיסמה',
        primaryButton: undefined,
        title: 'סיסמה',
      },
      passwordSection: {
        primaryButton__setPassword: 'הגדר סיסמה',
        primaryButton__updatePassword: 'שנה סיסמה',
        title: 'סיסמה',
      },
      phoneNumbersSection: {
        destructiveAction: 'הסר מספר טלפון',
        detailsAction__nonPrimary: 'הגדר כראשי',
        detailsAction__primary: 'השלם אימות',
        detailsAction__unverified: 'השלם אימות',
        primaryButton: 'הוסף מספר טלפון',
        title: 'מספרי טלפון',
      },
      profileSection: {
        primaryButton: 'עדכן פרופיל',
        title: 'פרופיל',
      },
      usernameSection: {
        primaryButton__setUsername: 'הגדר שם משתמש',
        primaryButton__updateUsername: 'שנה שם משתמש',
        title: 'שם משתמש',
      },
      web3WalletsSection: {
        destructiveAction: 'הסר ארנק',
        detailsAction__nonPrimary: undefined,
        primaryButton: 'ארנקי Web3',
        title: 'ארנקי Web3',
        web3SelectSolanaWalletScreen: {
          subtitle: 'בחר/י ארנק Solana כדי לחבר לחשבון שלך.',
          title: 'הוסף/י ארנק Solana',
        },
      },
    },
    usernamePage: {
      successMessage: 'שם המשתמש שלך עודכן.',
      title__set: 'עדכן שם משתמש',
      title__update: 'עדכן שם משתמש',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} יוסר מהחשבון הזה.',
        messageLine2: 'לא תוכל יותר להתחבר באמצעות ארנק web3 זה.',
        successMessage: '{{web3Wallet}} הוסר מהחשבון שלך.',
        title: 'הסר ארנק web3',
      },
      subtitle__availableWallets: 'בחר ארנק web3 לחיבור לחשבון שלך.',
      subtitle__unavailableWallets: 'אין ארנקי web3 זמינים.',
      successMessage: 'הארנק התווסף לחשבון שלך.',
      title: 'הוסף ארנק web3',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'התחבר',
      actionText: 'כבר יש לך גישה?',
      formButton: 'הצטרף לרשימת ההמתנה',
      subtitle: 'הזן את כתובת הדוא"ל שלך ואנו נודיע לך כשהמקום שלך יהיה מוכן',
      title: 'הצטרף לרשימת ההמתנה',
    },
    success: {
      message: 'תועבר בקרוב...',
      subtitle: 'אנו ניצור איתך קשר כשהמקום שלך יהיה מוכן',
      title: 'תודה שהצטרפת לרשימת ההמתנה!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'התחבר/י עם {{walletName}}',
    continue: 'המשך/י עם {{walletName}}',
    noneAvailable:
      'לא זוהו ארנקי Solana Web3. נא להתקין {{ solanaWalletsLink || link("wallet extension") }} התומך ב‑Web3.',
  },
} as const;
